import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "./logo.png";
import { isTeacherSubdomain, getCrossDomainUrl } from "./utils/subdomainDetector";
import { FaArrowRight, FaBars, FaTimes } from 'react-icons/fa';

// Helper function to get teacher subdomain URL
const getTeacherSubdomainUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname.includes('localhost')) {
    return 'http://teacher.localhost:3000/';
  }
  return 'https://teacher.taealam.bh';
};

function StripeNavbar({ lang, setLang, alwaysScrolled = false }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  
  const navigate = useNavigate();
  const location = useLocation();
  const onTeacherSubdomain = isTeacherSubdomain();
  const isArabic = lang === 'ar';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (navOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [navOpen]);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Determine features section ID based on subdomain
  const featuresSectionId = onTeacherSubdomain ? 'teacher-features-section' : 'why-choose-section';

  const navItems = isArabic ? [
    { label: 'الرئيسية', action: () => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
    { label: 'لماذا نحن', action: () => scrollToSection('why-choose-section') },
    { label: 'كيف يعمل', action: () => scrollToSection('how-it-works-section') },
    { label: 'المميزات', action: () => scrollToSection(featuresSectionId) },
    { label: 'تواصل معنا', action: () => scrollToSection('contact-us-section') },
    ...(onTeacherSubdomain ? [] : [{ label: 'كن معلماً', action: () => window.location.href = getTeacherSubdomainUrl() }]),
  ] : [
    { label: 'Home', action: () => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
    { label: 'Why Us', action: () => scrollToSection('why-choose-section') },
    { label: 'How It Works', action: () => scrollToSection('how-it-works-section') },
    { label: 'Features', action: () => scrollToSection(featuresSectionId) },
    { label: 'Contact', action: () => scrollToSection('contact-us-section') },
    ...(onTeacherSubdomain ? [] : [{ label: 'Become a Teacher', action: () => window.location.href = getTeacherSubdomainUrl() }]),
  ];

  return (
    <>
      <style>{`
        .stripe-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 9999;
          transition: all 0.3s ease;
        }
        
        .stripe-nav.scrolled {
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .stripe-nav-inner {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 40px;
          transition: padding 0.3s ease;
        }
        
        .stripe-nav.scrolled .stripe-nav-inner {
          padding: 12px 40px;
        }
        
        @media (max-width: 900px) {
          .stripe-nav-inner {
            padding: 14px 20px;
          }
        }
        
        .stripe-nav-links {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .stripe-nav-link {
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          font-weight: 500;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: transparent;
          border: none;
        }
        
        .stripe-nav-link:hover {
          color: white;
          background: rgba(255, 255, 255, 0.08);
        }
        
        .stripe-nav-btn {
          background: linear-gradient(135deg, #FF7426 0%, #FF9854 100%);
          color: white;
          font-size: 14px;
          font-weight: 600;
          padding: 10px 20px;
          border-radius: 100px;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(255, 116, 38, 0.3);
        }
        
        .stripe-nav-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 116, 38, 0.4);
        }
        
        .stripe-lang-btn {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: rgba(255, 255, 255, 0.7);
          font-size: 13px;
          font-weight: 500;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .stripe-lang-btn:hover {
          border-color: rgba(255, 255, 255, 0.3);
          color: white;
        }
        
        .stripe-hamburger {
          display: none;
          background: transparent;
          border: none;
          color: white;
          font-size: 22px;
          cursor: pointer;
          padding: 8px;
        }
        
        @media (max-width: 900px) {
          .stripe-nav-links, .stripe-nav-actions {
            display: none !important;
          }
          .stripe-hamburger {
            display: flex;
          }
        }
        
        .stripe-mobile-menu {
          position: fixed;
          top: 0;
          right: ${isArabic ? 'auto' : '0'};
          left: ${isArabic ? '0' : 'auto'};
          width: 100%;
          max-width: 320px;
          height: 100vh;
          background: #0F172A;
          z-index: 10001;
          transform: translateX(${isArabic ? '-100%' : '100%'});
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        
        .stripe-mobile-menu.open {
          transform: translateX(0);
        }
        
        .stripe-mobile-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          z-index: 10000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }
        
        .stripe-mobile-overlay.open {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
      
      {/* Navbar */}
      <nav className={`stripe-nav ${scrolled || alwaysScrolled ? 'scrolled' : ''}`} dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="stripe-nav-inner">
          {/* Logo */}
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/')}
          >
            <img 
              src={logo} 
              alt="Taealam" 
              style={{ 
                height: 56, 
                transition: 'height 0.3s ease',
                filter: 'brightness(0) invert(1)'
              }} 
            />
          </div>

          {/* Center Nav Links */}
          <div className="stripe-nav-links">
            {navItems.map((item, idx) => (
              <button
                key={idx}
                className="stripe-nav-link"
                onClick={item.action}
                style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="stripe-nav-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="stripe-lang-btn"
              onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
              style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
            >
              {isArabic ? 'EN' : 'العربية'}
            </button>
            <button
              className="stripe-nav-btn"
              onClick={() => navigate('/signin')}
              style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
            >
              {isArabic ? 'تسجيل الدخول' : 'Sign In'}
              <FaArrowRight size={12} style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button 
            className="stripe-hamburger"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
          >
            <FaBars />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`stripe-mobile-overlay ${navOpen ? 'open' : ''}`}
        onClick={() => setNavOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`stripe-mobile-menu ${navOpen ? 'open' : ''}`} dir={isArabic ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <img 
            src={logo} 
            alt="Taealam" 
            style={{ height: 28, filter: 'brightness(0) invert(1)' }} 
          />
          <button 
            onClick={() => setNavOpen(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: 20,
              cursor: 'pointer',
              padding: 8
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Mobile Nav Links */}
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
          {navItems.map((item, idx) => (
            <button
              key={idx}
              onClick={() => { item.action(); setNavOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: isArabic ? 'right' : 'left',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: 16,
                fontWeight: 500,
                padding: '14px 0',
                cursor: 'pointer',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif"
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Mobile Actions */}
        <div style={{ padding: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
          <button
            onClick={() => { navigate('/signin'); setNavOpen(false); }}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
              color: 'white',
              border: 'none',
              padding: '14px 20px',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 12,
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}
          >
            {isArabic ? 'تسجيل الدخول' : 'Sign In'}
            <FaArrowRight size={14} style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />
          </button>
          <button
            onClick={() => { setLang(lang === 'en' ? 'ar' : 'en'); setNavOpen(false); }}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              color: 'rgba(255, 255, 255, 0.7)',
              padding: '12px 20px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}
          >
            {isArabic ? 'English' : 'العربية'}
          </button>
        </div>
      </div>
    </>
  );
}

export default StripeNavbar;

